import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import moment from "moment";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { TwMainLayout } from "../../../components/tailwind";
import { classNames } from "../../../helpers";
import { useToast } from "../../../provider/ToastProvider";
import { setGlobalLoading } from "../../../redux/reducers/loadingReducer";
import { userState } from "../../../redux/reducers/userReducer";
import axiosInstance from "../../../utils/axiosInstance";
import { getUrlForBookings, getUrlForClasses } from "../../../utils/confighelpers";
import { Picker } from "../../dashboard/insights/shared";
import { Avatar, Badge, purchaseName } from "../members/memberUtils";
import { FcConfirmDialog, Icon } from "../schedule/ui";

/**
 * Bookings page (2026-10-28), route /bookings ("View all" on the dashboard's Recent bookings).
 * Every class booking members made, newest booked first (no other sorting, on purpose): who booked, when, which class
 * and session, paid with what, status. Search by member, filter by status and class, 25 at a time with "Show more".
 *
 *  - GET getMybookings?teacherId&page&limit=25&filterBy=all|confirmed|cancelled&listFor&classId?&search?
 *    (club lists use the backend's fast path: page first, then details; search = this club's members by name / email /
 *    phone, plus the name on the booking)
 *  - Cancel booking: same call as "Remove participant" in My Schedule (POST cancelClassBooking, type "remove");
 *    money or credits used are refunded in full. Only for booked sessions that haven't started yet.
 */

const PAGE_SIZE = 25;
const STATUS_FILTERS = [
  ["all", "All"],
  ["confirmed", "Booked"],
  ["cancelled", "Cancelled"],
];
const I = {
  search: <><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></>,
  user: <><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0 1 16 0" /></>,
  x: <><circle cx="12" cy="12" r="9" /><path d="m15 9-6 6M9 9l6 6" /></>,
  cal: <><rect x="3" y="4" width="18" height="18" rx="3" /><path d="M16 2v4M8 2v4M3 10h18" /></>,
};
const DOTS = (
  <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
    <circle cx="12" cy="5" r="1.6" fill="currentColor" />
    <circle cx="12" cy="12" r="1.6" fill="currentColor" />
    <circle cx="12" cy="19" r="1.6" fill="currentColor" />
  </svg>
);

/** What the list shows for one booking (getMybookings row). */
const view = (b) => {
  const student = b.student_detail || {};
  const cancelled = b.status === "Cancelled";
  const session = b.schedule_class_detail?.classDate ? moment(b.schedule_class_detail.classDate) : null;
  // bookingDate is set by the booking code; createdAt isn't reliable (models use default: Date.now()).
  const bookedAt = moment(b.bookingDate || b.createdAt);
  return {
    name: b.familyMemberName || student.fullName || b.studentName || "Member",
    familyOf: b.familyMemberName && student.fullName ? student.fullName : "",
    userId: student._id || b.studentId,
    profilePic: student.profilePic,
    className: b.className || b.schedule_class_detail?.className || b.class_detail?.className || "Class",
    session,
    bookedAt,
    cancelledAt: cancelled && b.cancellationDate ? moment(b.cancellationDate) : null,
    cancelled,
    paidWith: b.purchase_voucher_detail ? purchaseName(b.purchase_voucher_detail) : b.paymentMethod && b.paymentMethod !== "classPass" && b.paymentMethod !== "NA" ? "Paid per class" : "–",
    canCancel: !cancelled && session && session.isAfter(moment()),
  };
};

export default function BookingsPage({ title = "Bookings" }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { addSuccessToast, addErrorToast } = useToast();
  const userReduxState = useSelector(userState);
  const businessId = userReduxState.userData?._id;
  const listFor = userReduxState.businessType || "class";

  const [searchInput, setSearchInput] = React.useState("");
  const [search, setSearch] = React.useState("");
  const [status, setStatus] = React.useState("all");
  const [classId, setClassId] = React.useState("all");
  const [classes, setClasses] = React.useState([]);
  const [rows, setRows] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [page, setPage] = React.useState(1);
  const [loading, setLoading] = React.useState(true);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [error, setError] = React.useState("");
  const [cancelling, setCancelling] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const membersCacheRef = React.useRef(null);
  const reqRef = React.useRef(0);

  // Search from 2 characters (or cleared), after typing stops.
  React.useEffect(() => {
    const q = searchInput.trim();
    const t = setTimeout(() => setSearch(q.length >= 2 ? q : ""), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axiosInstance({ method: "get", url: `${getUrlForClasses()}listOfClasses?isPackage=${false}&listFor=${listFor}&onlyClassDetail=true` });
        if (alive && res?.status === 200 && Array.isArray(res.data?.data)) setClasses(res.data.data.map((c) => ({ value: c._id, label: c.className })));
      } catch (e) {
        /* the class filter just stays at "All classes" */
      }
    })();
    return () => {
      alive = false;
    };
  }, [listFor]);

  const load = React.useCallback(
    async (p = 1) => {
      if (!businessId) return;
      const id = ++reqRef.current;
      p === 1 ? setLoading(true) : setLoadingMore(true);
      setError("");
      try {
        const qs = new URLSearchParams({ teacherId: businessId, page: String(p), limit: String(PAGE_SIZE), filterBy: status, listFor });
        if (classId !== "all") qs.set("classId", classId);
        if (search) qs.set("search", search);
        const res = await axiosInstance({ method: "get", url: `${getUrlForBookings()}getMybookings?${qs.toString()}` });
        if (id !== reqRef.current) return; // a newer search / filter already started
        const data = res?.status === 200 && res?.data?.statusCode === 200 ? res.data.data : null;
        if (!data) throw new Error("bad response");
        const list = Array.isArray(data.data) ? data.data : [];
        setRows((prev) => (p === 1 ? list : [...prev, ...list.filter((x) => !prev.some((y) => y._id === x._id))]));
        setTotal(Number(data.total) || 0);
        setPage(p);
      } catch (e) {
        if (id !== reqRef.current) return;
        if (p === 1) setRows([]);
        setError(p === 1 ? "Couldn't load the bookings." : "Couldn't load more bookings.");
      } finally {
        if (id === reqRef.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [businessId, status, classId, search, listFor]
  );

  React.useEffect(() => {
    load(1);
  }, [load]);

  const openMember = async (v) => {
    try {
      if (!membersCacheRef.current) {
        dispatch(setGlobalLoading(true));
        const res = await axiosInstance({ method: "get", url: `${getUrlForClasses("")}getMyClubMembers?invite=used&getall=true&businessId=${businessId}` });
        dispatch(setGlobalLoading(false));
        const raw = res?.data?.data;
        membersCacheRef.current = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
      }
      const member = membersCacheRef.current.find((m) => String(m?.consumer_detail?.consumerId || m?.consumerId || "") === String(v.userId));
      if (member) navigate(`/mymembers/detail?id=${v.userId}&mid=${member._id}`);
      else addErrorToast("This person isn't in your member list.");
    } catch (e) {
      dispatch(setGlobalLoading(false));
      addErrorToast("Couldn't open this member.");
    }
  };

  const cancelBooking = async () => {
    const b = cancelling;
    if (!b) return;
    setBusy(true);
    try {
      const res = await axiosInstance({
        method: "post",
        url: `${getUrlForBookings()}cancelClassBooking`,
        data: { bookingId: b.bookingId, scheduleClassId: b.scheduleClassId, type: "remove", isPackage: b.isPackage || false, participantId: b.studentId, familyMemberId: "" },
      });
      if (res?.status === 200 && res?.data?.statusCode === 200) {
        const v = view(b);
        addSuccessToast(`${v.name}'s booking is cancelled`);
        setRows((prev) => prev.map((x) => (x._id === b._id ? { ...x, status: "Cancelled", cancellationDate: new Date().toISOString() } : x)));
        setCancelling(null);
      } else {
        addErrorToast((typeof res?.data?.message === "string" && res.data.message) || "Couldn't cancel this booking.");
      }
    } catch (e) {
      addErrorToast("Couldn't reach the server. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const filtered = search || status !== "all" || classId !== "all";
  const hasMore = rows.length > 0 && rows.length < total;
  const cv = cancelling ? view(cancelling) : null;

  return (
    <TwMainLayout
      children={
        <div className="flex-1 px-4 pb-16 pt-6 sm:px-8 lg:px-10">
          <div>
            <h1 className="text-2xl font-black tracking-tight text-gray-900 sm:text-3xl">{title}</h1>
            <p className="mt-1 max-w-xl text-sm text-gray-600 sm:text-base">Every booking your members made, newest first. Search who booked, filter by class, and cancel a booking when needed.</p>
          </div>

          {/* Toolbar */}
          <div className="mt-6 flex flex-col gap-3 lg:flex-row lg:items-center">
            <div className="relative w-full lg:w-80">
              <span className="pointer-events-none absolute inset-y-0 left-4 flex items-center text-gray-400">
                <Icon className="h-5 w-5" d={I.search} />
              </span>
              <input
                type="search"
                aria-label="Search who booked"
                placeholder="Search name, email or phone"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="block h-12 w-full rounded-2xl border border-gray-200 bg-white pl-11 pr-4 text-base text-gray-900 outline-none placeholder:text-gray-400 focus:border-indigo-bz focus:ring-4 focus:ring-[#F3E9FF]"
              />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div role="tablist" aria-label="Booking status" className="flex gap-1 rounded-2xl bg-[#F3EEF5] p-1">
                {STATUS_FILTERS.map(([k, l]) => (
                  <button
                    key={k}
                    type="button"
                    role="tab"
                    aria-selected={status === k}
                    onClick={() => setStatus(k)}
                    className={classNames("h-9 shrink-0 whitespace-nowrap rounded-xl px-3.5 text-sm font-extrabold", status === k ? "bg-white text-brown-coffee shadow-sm" : "text-gray-500 hover:text-gray-900")}
                  >
                    {l}
                  </button>
                ))}
              </div>
              <Picker label="Class" value={classId} options={[{ value: "all", label: "All classes" }, ...classes]} onChange={setClassId} align="start" />
            </div>
            <span className="text-sm font-bold text-gray-600 lg:ml-auto">{loading ? "" : `${total.toLocaleString("nl-NL")} ${total === 1 ? "booking" : "bookings"}`}</span>
          </div>

          {/* List */}
          <div className="mt-5 rounded-3xl border border-gray-200 bg-white shadow-[0_10px_30px_rgba(72,27,58,0.05)]">
            <div className="hidden grid-cols-[minmax(0,1.1fr)_minmax(0,1.7fr)_minmax(0,1.7fr)_minmax(0,1.1fr)_minmax(0,0.8fr)_44px] gap-3 px-5 pb-2.5 pt-4 md:grid">
              {["Booked", "Member", "Class & session", "Paid with", "Status", ""].map((h, i) => (
                <span key={i} className="text-xs font-extrabold uppercase tracking-wide text-gray-500">{h}</span>
              ))}
            </div>
            {loading ? (
              <div aria-hidden="true">
                {[0, 1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="flex animate-pulse items-center gap-3 border-t border-gray-100 px-5 py-4 first:border-t-0 md:first:border-t">
                    <span className="hidden h-4 w-24 rounded-full bg-[#F3EEF5] md:block" />
                    <span className="h-10 w-10 shrink-0 rounded-full bg-[#EDE5F0]" />
                    <span className="flex-1">
                      <span className="block h-4 w-40 rounded-full bg-[#EDE5F0]" />
                      <span className="mt-2 block h-3 w-56 max-w-full rounded-full bg-[#F3EEF5]" />
                    </span>
                    <span className="h-5 w-20 rounded-full bg-[#F3EEF5]" />
                  </div>
                ))}
              </div>
            ) : error && rows.length === 0 ? (
              <div className="flex flex-col items-center gap-2 border-t border-gray-100 px-6 py-12 text-center text-sm text-gray-600 md:border-t">
                {error}
                <button type="button" onClick={() => load(1)} className="rounded-xl px-3 py-1.5 text-sm font-extrabold text-indigo-bz hover:bg-[#F3E9FF]">
                  Try again
                </button>
              </div>
            ) : rows.length === 0 ? (
              <div className="border-t border-gray-100 px-6 py-12 text-center text-sm text-gray-600 md:border-t">
                {search ? `No bookings by members matching “${search}”.` : filtered ? "No bookings match these filters." : "No bookings yet."}
              </div>
            ) : (
              <ul>
                {rows.map((b) => (
                  <BookingRow key={b._id} b={b} onOpen={(v) => openMember(v)} onCancel={() => setCancelling(b)} />
                ))}
              </ul>
            )}
            {!loading && (hasMore || (error && rows.length > 0)) && (
              <div className="flex flex-col items-center gap-1 border-t border-gray-100 p-3">
                {error && rows.length > 0 && <span className="text-xs font-bold text-[#B4232F]">{error}</span>}
                {hasMore && (
                  <button type="button" disabled={loadingMore} onClick={() => load(page + 1)} className="flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-extrabold text-indigo-bz hover:bg-[#F3E9FF] disabled:opacity-60">
                    {loadingMore && <span className="h-4 w-4 animate-spin rounded-full border-2 border-[#E3D0F7] border-t-indigo-bz" />}
                    Show more bookings
                    <span className="font-bold text-gray-500">({rows.length} of {total.toLocaleString("nl-NL")})</span>
                  </button>
                )}
              </div>
            )}
          </div>

          <FcConfirmDialog
            open={!!cancelling}
            danger
            busy={busy}
            title="Cancel this booking?"
            message={cv ? `${cv.name} is removed from ${cv.className} on ${cv.session ? cv.session.format("ddd D MMM, HH:mm") : "this session"}. Any money or credits used for it are refunded in full.` : ""}
            confirmLabel="Cancel booking"
            cancelLabel="Keep it"
            onClose={() => !busy && setCancelling(null)}
            onConfirm={cancelBooking}
          />
        </div>
      }
    />
  );
}

function BookingRow({ b, onOpen, onCancel }) {
  const v = view(b);
  const items = [
    { label: "Open member", icon: I.user, onClick: () => onOpen(v) },
    ...(v.canCancel ? [{ label: "Cancel booking", icon: I.x, danger: true, onClick: onCancel }] : []),
  ];
  return (
    <li className="grid grid-cols-[minmax(0,1fr)_auto_44px] items-center gap-x-3 gap-y-1 border-t border-gray-100 px-4 py-3.5 first:border-t-0 sm:px-5 md:grid-cols-[minmax(0,1.1fr)_minmax(0,1.7fr)_minmax(0,1.7fr)_minmax(0,1.1fr)_minmax(0,0.8fr)_44px] md:first:border-t">
      {/* Booked (desktop) */}
      <span className="hidden min-w-0 md:block">
        <span className="block text-sm font-bold text-gray-900">{v.bookedAt.isValid() ? v.bookedAt.format("D MMM, HH:mm") : "–"}</span>
        <span className="block text-xs text-gray-500">{v.bookedAt.isValid() ? v.bookedAt.fromNow() : ""}</span>
      </span>
      {/* Member */}
      <button type="button" onClick={() => onOpen(v)} className="col-start-1 row-start-1 flex min-w-0 items-center gap-3 text-left md:col-start-auto md:row-start-auto">
        <Avatar src={v.profilePic} name={v.name} size={40} />
        <span className="min-w-0">
          <span className="block truncate text-[15px] font-extrabold text-gray-900 hover:text-indigo-bz">{v.name}</span>
          {v.familyOf ? <span className="block truncate text-xs text-gray-500">Family of {v.familyOf}</span> : null}
          <span className="block truncate text-xs text-gray-500 md:hidden">Booked {v.bookedAt.isValid() ? v.bookedAt.fromNow() : ""}</span>
        </span>
      </button>
      {/* Status (phone: top right) */}
      <span className="col-start-2 row-start-1 md:hidden">
        <Badge tone={v.cancelled ? "red" : "green"}>{v.cancelled ? "Cancelled" : "Booked"}</Badge>
      </span>
      {/* Class & session */}
      <span className="col-span-3 col-start-1 row-start-2 min-w-0 pl-[52px] md:col-span-1 md:col-start-auto md:row-start-auto md:pl-0">
        <span className="block truncate text-sm font-bold text-gray-900">{v.className}</span>
        <span className="block truncate text-xs text-gray-500">
          {v.session ? v.session.format("ddd D MMM, HH:mm") : "–"}
          <span className="md:hidden">{v.paidWith !== "–" ? ` · ${v.paidWith}` : ""}</span>
        </span>
      </span>
      {/* Paid with (desktop) */}
      <span className="hidden truncate text-sm text-gray-700 md:block">{v.paidWith}</span>
      {/* Status (desktop) */}
      <span className="hidden md:block">
        <Badge tone={v.cancelled ? "red" : "green"}>{v.cancelled ? "Cancelled" : "Booked"}</Badge>
        {v.cancelledAt && <span className="mt-1 block text-[11px] text-gray-500">{v.cancelledAt.fromNow()}</span>}
      </span>
      {/* Actions */}
      <Menu as="div" className="relative col-start-3 row-start-1 flex justify-end md:col-start-auto md:row-start-auto">
        <MenuButton aria-label={`Options for ${v.name}'s booking`} className="flex h-10 w-10 items-center justify-center rounded-xl text-gray-500 hover:bg-gray-100 data-[open]:bg-gray-100">
          {DOTS}
        </MenuButton>
        <MenuItems anchor="bottom end" className="z-[60] mt-1 w-56 rounded-2xl border border-gray-200 bg-white p-1.5 shadow-xl focus:outline-none">
          {items.map((it) => (
            <MenuItem key={it.label}>
              <button
                type="button"
                onClick={it.onClick}
                className={classNames("flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-sm font-bold", it.danger ? "text-[#B4232F] data-[focus]:bg-[#FFF5F6]" : "text-gray-900 data-[focus]:bg-[#F7F4F8]")}
              >
                <Icon className={classNames("h-4 w-4", it.danger ? "" : "text-gray-500")} d={it.icon} />
                {it.label}
              </button>
            </MenuItem>
          ))}
        </MenuItems>
      </Menu>
    </li>
  );
}
